import { memo, useState, useEffect } from 'react';
import { Text, BlockStack, Box, InlineStack, Badge, Link, SkeletonBodyText } from '@shopify/polaris';
import { handleOpenChat } from '@/helpers';
import {
  DrawerOverlay,
  DrawerContainer,
  DrawerHeader,
  DrawerHeaderLeft,
  DrawerContent,
  CloseButton,
  InsightCard,
} from './styled';

interface TrakowAssistantDrawerProps {
  open: boolean;
  onClose: () => void;
  // New optional props for dynamic data
  data?: {
    insights: Array<{ type: 'positive' | 'negative' | 'neutral'; text: string }>;
    suggestions: Array<{ text: string }>;
    periodFrom: string;
    periodTo: string;
  };
  loading?: boolean;
}

const TrakowAssistantDrawer = ({ open, onClose, data, loading = false }: TrakowAssistantDrawerProps) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsClosing(false);
    }
  }, [open]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open && !isClosing) return null;

  // Map data to component format
  const insights = data?.insights.map(insight => ({
    text: insight.text,
    sentiment: insight.type,
  })) || [];

  const actionPlans = data?.suggestions.map(s => s.text) || [];

  // Format date range for display
  const formatDateRange = () => {
    if (!data) return '';

    const formatDate = (isoString: string) => {
      const date = new Date(isoString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    return `From ${formatDate(data.periodFrom)} to ${formatDate(data.periodTo)}`;
  };

  return (
    <>
      <DrawerOverlay $isOpen={open} onClick={handleClose} />
      <DrawerContainer $isOpen={open} $isClosing={isClosing}>
        <DrawerHeader>
          <DrawerHeaderLeft>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_3701_11764)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.63428 18.9269C3.63429 18.6427 3.74717 18.3702 3.9481 18.1693C4.14902 17.9684 4.42153 17.8555 4.70568 17.8555H9.70568C9.98872 17.8572 10.2596 17.9708 10.4591 18.1716C10.6587 18.3723 10.7707 18.6439 10.7707 18.9269C10.7707 19.2099 10.6587 19.4815 10.4591 19.6822C10.2596 19.883 9.98872 19.9966 9.70568 19.9983H4.70568C4.42153 19.9983 4.14901 19.8854 3.94808 19.6845C3.74716 19.4836 3.63428 19.211 3.63428 18.9269Z"
                  fill="url(#paint0_linear_3701_11764)"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.88 11.4801C12.3085 12.2258 11.5943 12.8558 10.7771 13.3286V14.9986C10.7771 15.1862 10.7402 15.372 10.6684 15.5453C10.5966 15.7187 10.4913 15.8761 10.3587 16.0088C10.226 16.1414 10.0685 16.2467 9.89522 16.3185C9.72189 16.3902 9.53612 16.4272 9.34852 16.4272H5.06286C4.87525 16.4272 4.68949 16.3902 4.51616 16.3185C4.34284 16.2467 4.18535 16.1414 4.05269 16.0088C3.92003 15.8761 3.8148 15.7187 3.74301 15.5453C3.67122 15.372 3.63426 15.1862 3.63426 14.9986V13.3286C2.7451 12.8146 1.97725 12.1148 1.38307 11.2771C0.788898 10.4394 0.382371 9.48335 0.191214 8.47424C5.73248e-05 7.46512 0.0287678 6.42666 0.275388 5.42965C0.522007 4.43264 0.980736 3.50054 1.62029 2.69691C2.25985 1.89328 3.06519 1.23703 3.98139 0.772898C4.8976 0.308768 5.90312 0.0476805 6.92939 0.00743854C7.95566 -0.0328035 8.97854 0.148747 9.92827 0.539709C10.878 0.930671 11.7323 1.52185 12.4328 2.27293C12.3505 2.62561 12.166 2.94622 11.9026 3.1947C11.6391 3.44318 11.3083 3.60853 10.9514 3.67008C7.99137 4.18434 7.99137 8.43434 10.9514 8.94863C11.6899 9.07719 12.2799 9.64149 12.4399 10.3743L12.4671 10.5001C12.5485 10.8772 12.6928 11.2044 12.8814 11.4801H12.88Z"
                  fill="url(#paint1_linear_3701_11764)"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16.2187 2.44291C15.9473 1.2515 14.2458 1.25721 13.9844 2.45291L13.9744 2.50006L13.953 2.59291C13.8061 3.24937 13.4677 3.84748 12.9807 4.31157C12.4938 4.77566 11.8801 5.08487 11.2173 5.20006C9.97444 5.41721 9.97444 7.20291 11.2173 7.41861C11.8833 7.53432 12.4997 7.84593 12.9877 8.31363C13.4758 8.78133 13.8133 9.38389 13.9573 10.0444L13.9844 10.1686C14.2458 11.3643 15.9473 11.3715 16.2187 10.1786L16.2516 10.0329C16.4018 9.3753 16.7429 8.77673 17.2322 8.31237C17.7215 7.84802 18.337 7.53857 19.0016 7.42291C20.2487 7.20576 20.2487 5.41721 19.0016 5.20006C18.3214 5.08202 17.6928 4.76096 17.1984 4.27908C16.704 3.7972 16.367 3.17703 16.2316 2.50006L16.2187 2.44291Z"
                  fill="url(#paint2_linear_3701_11764)"
                />
              </g>
              <defs>
                <linearGradient
                  id="paint0_linear_3701_11764"
                  x1="3.67184"
                  y1="19.9983"
                  x2="10.8291"
                  y2="17.9277"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#5101FE" />
                  <stop offset="1" stopColor="#9E28E8" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_3701_11764"
                  x1="0.133854"
                  y1="16.4272"
                  x2="13.9984"
                  y2="15.4875"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#5101FE" />
                  <stop offset="1" stopColor="#9E28E8" />
                </linearGradient>
                <linearGradient
                  id="paint2_linear_3701_11764"
                  x1="10.336"
                  y1="11.0693"
                  x2="20.7454"
                  y2="10.1522"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#5101FE" />
                  <stop offset="1" stopColor="#9E28E8" />
                </linearGradient>
                <clipPath id="clip0_3701_11764">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <Text as="h2" variant="headingMd" fontWeight="semibold">
              Trakow Assistant
            </Text>
            <Badge tone="success">Beta</Badge>
          </DrawerHeaderLeft>
          <CloseButton onClick={handleClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 5L15 15M5 15L15 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          <BlockStack gap="300">
            {/* Date Range - only show if data exists */}
            {data && (
              <Text as="p" variant="bodySm" tone="subdued">
                {formatDateRange()}
              </Text>
            )}

            {/* Loading State */}
            {loading && (
              <Box paddingBlockStart="400">
                <BlockStack gap="300">
                  <SkeletonBodyText lines={5} />
                  <SkeletonBodyText lines={3} />
                  <SkeletonBodyText lines={5} />
                </BlockStack>
              </Box>
            )}

            {/* Insights and Suggestions - only show if not loading and data exists */}
            {!loading && data && (
              <>
                {/* Key Insights Section */}
                {insights.length > 0 && (
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Key insights
                    </Text>
                    <BlockStack gap="300">
                      {insights.map((insight, index) => {
                  const borderColor =
                    insight.sentiment === 'positive'
                      ? '#29845A'
                      : insight.sentiment === 'neutral'
                        ? '#0094D5'
                        : '#B28400';
                  return (
                    <InsightCard key={index} $borderColor={borderColor}>
                      <InlineStack gap="300" blockAlign="start" wrap={false}>
                        <Box minWidth="20px">
                          {insight.sentiment === 'positive' && (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M4.69774 15.303C4.35987 14.9652 4.35987 14.4174 4.69774 14.0795L12.6015 6.17576L8.16447 6.17598C7.68665 6.17601 7.29928 5.78867 7.29925 5.31085C7.29923 4.83303 7.68656 4.44566 8.16438 4.44564L14.6902 4.44531C14.9197 4.4453 15.1398 4.53645 15.302 4.69871C15.4643 4.86098 15.5555 5.08105 15.5554 5.31053L15.5551 11.8364C15.5551 12.3142 15.1677 12.7015 14.6899 12.7015C14.2121 12.7015 13.8248 12.3141 13.8248 11.8363L13.825 7.39929L5.92127 15.303C5.5834 15.6409 5.03561 15.6409 4.69774 15.303Z"
                                fill="#29845A"
                              />
                            </svg>
                          )}
                          {insight.sentiment === 'neutral' && (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M9.99998 14C9.58577 14 9.24999 13.6642 9.25 13.25L9.25006 9.74999C9.25007 9.33577 9.58586 8.99999 10.0001 9C10.4143 9.00001 10.7501 9.3358 10.7501 9.75001L10.75 13.25C10.75 13.6642 10.4142 14 9.99998 14Z"
                                fill="#0094D5"
                              />
                              <path
                                d="M9 7C9 6.44772 9.44772 6 10 6C10.5523 6 11 6.44772 11 7C11 7.55228 10.5523 8 10 8C9.44772 8 9 7.55228 9 7Z"
                                fill="#0094D5"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10ZM15.5 10C15.5 13.0376 13.0376 15.5 10 15.5C6.96243 15.5 4.5 13.0376 4.5 10C4.5 6.96243 6.96243 4.5 10 4.5C13.0376 4.5 15.5 6.96243 15.5 10Z"
                                fill="#0094D5"
                              />
                            </svg>
                          )}
                          {insight.sentiment === 'negative' && (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M4.69774 4.69698C4.35987 5.03485 4.35987 5.58265 4.69774 5.92052L12.6015 13.8242L8.16447 13.824C7.68665 13.824 7.29928 14.2113 7.29925 14.6891C7.29923 15.167 7.68656 15.5543 8.16438 15.5544L14.6902 15.5547C14.9197 15.5547 15.1398 15.4635 15.302 15.3013C15.4643 15.139 15.5555 14.9189 15.5554 14.6895L15.5551 8.16362C15.5551 7.6858 15.1677 7.29847 14.6899 7.29849C14.2121 7.29852 13.8248 7.68589 13.8248 8.16371L13.825 12.6007L5.92127 4.69698C5.5834 4.35911 5.03561 4.35911 4.69774 4.69698Z"
                                fill="#B28400"
                              />
                            </svg>
                          )}
                        </Box>
                        <Text as="p" variant="bodyMd">
                          {insight.text}
                        </Text>
                      </InlineStack>
                    </InsightCard>
                      );
                      })}
                    </BlockStack>
                  </BlockStack>
                )}

                {/* Suggested Action Plan Section */}
                {actionPlans.length > 0 && (
                  <BlockStack gap="300">
                    <Text as="h3" variant="headingSm" fontWeight="semibold">
                      Suggested action plan
                    </Text>
                    <BlockStack gap="300">
                      {actionPlans.map((action, index) => (
                  <InsightCard key={index}>
                    <InlineStack gap="300" blockAlign="start" wrap={false}>
                      <Box minWidth="20px">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M10 2C10.4142 2 10.75 2.33579 10.75 2.75V3.25C10.75 3.66421 10.4142 4 10 4C9.58579 4 9.25 3.66421 9.25 3.25V2.75C9.25 2.33579 9.58579 2 10 2Z"
                            fill="#4A4A4A"
                          />
                          <path
                            d="M5.58059 4.16656C5.2877 3.87367 4.81283 3.87367 4.51993 4.16656C4.22704 4.45945 4.22704 4.93433 4.51993 5.22722L4.87349 5.58077C5.16638 5.87367 5.64125 5.87367 5.93415 5.58077C6.22704 5.28788 6.22704 4.81301 5.93415 4.52011L5.58059 4.16656Z"
                            fill="#4A4A4A"
                          />
                          <path
                            d="M2 9.75C2 9.33579 2.33579 9 2.75 9H3.25C3.66421 9 4 9.33579 4 9.75C4 10.1642 3.66421 10.5 3.25 10.5H2.75C2.33579 10.5 2 10.1642 2 9.75Z"
                            fill="#4A4A4A"
                          />
                          <path
                            d="M16 9.75C16 9.33579 16.3358 9 16.75 9H17.25C17.6642 9 18 9.33579 18 9.75C18 10.1642 17.6642 10.5 17.25 10.5H16.75C16.3358 10.5 16 10.1642 16 9.75Z"
                            fill="#4A4A4A"
                          />
                          <path
                            d="M15.6569 5.40398C15.9497 5.11109 15.9497 4.63621 15.6569 4.34332C15.364 4.05043 14.8891 4.05043 14.5962 4.34332L14.2426 4.69687C13.9497 4.98976 13.9497 5.46464 14.2426 5.75753C14.5355 6.05043 15.0104 6.05043 15.3033 5.75753L15.6569 5.40398Z"
                            fill="#4A4A4A"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M6.4737 5.99824C8.42123 4.05072 11.5788 4.05072 13.5263 5.99824C14.4753 6.94719 14.9869 8.10141 14.9869 9.29552C14.9869 10.4896 14.4753 11.6438 13.5263 12.5928C13.5085 12.6106 13.4905 12.6282 13.4725 12.6456C12.9997 13.1009 12.75 13.5121 12.75 13.8864V14.9999C12.75 16.3806 11.6307 17.4999 10.25 17.4999H9.75001C8.3693 17.4999 7.25001 16.3806 7.25001 14.9999V13.8864C7.25001 13.5121 7.00032 13.1009 6.52755 12.6456C6.50949 12.6282 6.49154 12.6106 6.4737 12.5928C5.52476 11.6438 5.01306 10.4896 5.01306 9.29552C5.01306 8.10141 5.52476 6.94719 6.4737 5.99824ZM12.4656 7.0589C11.1039 5.69716 8.8961 5.69716 7.53436 7.0589C6.82868 7.76458 6.51306 8.54999 6.51306 9.29552C6.51306 10.041 6.82868 10.8265 7.53436 11.5321C7.54555 11.5433 7.55678 11.5543 7.56805 11.5652C7.93411 11.9177 8.35586 12.4006 8.58345 13H11.4166C11.6442 12.4006 12.0659 11.9177 12.432 11.5652C12.4432 11.5543 12.4545 11.5433 12.4656 11.5321C13.1713 10.8265 13.4869 10.041 13.4869 9.29552C13.4869 8.54999 13.1713 7.76458 12.4656 7.0589ZM11.25 14.5H8.75001V14.9999C8.75001 15.5522 9.19772 15.9999 9.75001 15.9999H10.25C10.8023 15.9999 11.25 15.5522 11.25 14.9999V14.5Z"
                            fill="#4A4A4A"
                          />
                        </svg>
                      </Box>
                      <Text as="p" variant="bodyMd">
                        {action}
                      </Text>
                    </InlineStack>
                      </InsightCard>
                      ))}
                    </BlockStack>
                  </BlockStack>
                )}

                {/* Feedback Section */}
                <Box paddingBlockStart="400">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="p" variant="bodySm" tone="subdued">
                      Not find this helpful?
                    </Text>
                    <Link url="#" removeUnderline onClick={handleOpenChat}>
                      Feedback us
                    </Link>
                  </InlineStack>
                </Box>
              </>
            )}
          </BlockStack>
        </DrawerContent>
      </DrawerContainer>
    </>
  );
};

export default memo(TrakowAssistantDrawer);
